import { useState, useMemo } from "react";
import { supportDirectory } from "../lib/content";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import { ArrowUpRight } from "lucide-react";

const ALL_TYPES = [
  "All",
  ...Array.from(new Set(supportDirectory.map((s) => s.type))),
];

export default function SupportPage() {
  const [filter, setFilter] = useState("All");

  const items = useMemo(
    () =>
      filter === "All"
        ? supportDirectory
        : supportDirectory.filter((s) => s.type === filter),
    [filter, supportDirectory]
  );

  return (
    <div data-testid="support-page">
      <PageMeta
        title="Find support"
        description="Ten trusted UK organisations who can stand beside you — solicitors, advocates, kinship care support, and emotional-support helplines."
        path="/support"
      />
      <PageHeader
        overline="Find support today"
        title="The people who can help."
        lede="A short, honest list of organisations we trust. None of them paid to be here — and none of them can replace a solicitor for your specific case. But they are good places to begin."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div
          className="flex flex-wrap gap-2 mb-10"
          data-testid="support-filter-bar"
        >
          {ALL_TYPES.map((t) => (
            <button
              key={t}
              data-testid={`filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-button`}
              onClick={() => setFilter(t)}
              className={`px-4 py-2 text-sm font-accent border transition-colors ${
                filter === t
                  ? "bg-ink text-linen border-ink"
                  : "border-rule text-ink-muted hover:border-ink hover:text-ink"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div
          data-testid="support-list"
          className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule"
        >
          {items.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`support-${s.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-link`}
              className="group bg-linen hover:bg-white p-8 md:p-10 transition-colors flex flex-col"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
                    {s.type}
                  </div>
                  <h3 className="font-serif text-2xl md:text-[1.7rem] mt-3 text-ink tracking-tight leading-tight">
                    {s.name}
                  </h3>
                </div>
                <ArrowUpRight
                  size={20}
                  strokeWidth={1.5}
                  className="flex-shrink-0 text-ink-muted group-hover:text-river group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </div>
              <p className="mt-5 text-ink-muted leading-relaxed flex-1">
                {s.description}
              </p>
              <div className="mt-6 text-xs font-accent text-ink-muted">
                {s.region}
              </div>
            </a>
          ))}
        </div>

        <div className="mt-16 max-w-3xl border-l-2 border-ember pl-6 md:pl-8">
          <p className="font-serif text-xl md:text-2xl text-ink leading-snug">
            If you do not know where to start, call Family Rights Group on{" "}
            <span className="text-river whitespace-nowrap">0808 801 0366</span>.
          </p>
          <p className="mt-3 text-ink-muted leading-relaxed">
            Their advice line is free, confidential, and run by people who specialise in exactly the situation you are in.
          </p>
        </div>

        <div className="mt-16">
          <LastReviewed path="/support" />
        </div>
      </section>
    </div>
  );
}
